/**
 * Player Tournaments: URL ?tenant_id= &campaign_id= &season_id= &team_id= &view=
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import styled from 'styled-components';
import { Trans, useTranslation } from 'react-i18next';
import { playerAPI } from '../../services/api';
import DashboardPage from '../../pages/DashboardPage';
import CampaignPosterView from './CampaignPosterView';
import TournamentFlowPickers from './TournamentFlowPickers';
import RegistrationCampaignPanel from './RegistrationCampaignPanel';
import PlayerLeaguePerformanceSchedule from './PlayerLeaguePerformanceSchedule';
import PlayerOrganisedTournamentsSection from './PlayerOrganisedTournamentsSection';
import PlayerEventsHub from './PlayerEventsHub';
import PlayerEventsCampsPlaceholder from './PlayerEventsCampsPlaceholder';
import {
  campaignEligibleForPlayerRegistration,
  filterRegistrationEligibleCampaigns,
  findHelveticPadelSeriesTenant,
  HELVETIC_AUTO_TENANT_QUERY,
  pickPrimaryRegistrationSeasonId,
  seasonRowRegistrationOpen,
} from '../../utils/tournamentRegistrationHelpers';

const VIEW_TO_SECTION = {
  overview: 'overview',
  statistics: 'stats',
  schedule: 'schedule',
  results: 'results',
  rankings: 'divisions',
  'reserve-court': 'reserve-court',
  'team-members': 'team',
  settings: 'settings',
};

/** Dashboard internal section key → tournaments URL `view` param */
const SECTION_TO_VIEW = {
  overview: 'overview',
  stats: 'statistics',
  schedule: 'schedule',
  results: 'results',
  divisions: 'rankings',
  'reserve-court': 'reserve-court',
  team: 'team-members',
  settings: 'settings',
};

const ContentArea = styled.div`
  flex: 1;
  min-width: 0;
  overflow: auto;
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 55%, #eef2f7 100%);
  display: flex;
  flex-direction: column;
  padding: ${(p) => (p.$embed ? 0 : 'clamp(1rem, 3vw, 2.5rem)')};
`;

const PageTitle = styled.h1`
  font-size: clamp(1.5rem, 3.5vw, 2rem);
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 0.5rem 0;
  flex-shrink: 0;
  letter-spacing: -0.03em;
`;

const PageSubtitle = styled.p`
  margin: 0 0 1.5rem;
  font-size: 0.9375rem;
  color: #64748b;
  line-height: 1.55;
  max-width: 48rem;
`;

const PerformanceScheduleSection = styled.div`
  margin-top: 1.5rem;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 220px;
  color: #64748b;
  font-size: 0.9375rem;
  text-align: center;
  padding: 2rem;
`;

const Hint = styled.p`
  color: #64748b;
  font-size: 0.9375rem;
  text-align: center;
  max-width: 480px;
  margin: 2rem auto;
  line-height: 1.55;
`;

const ActionButton = styled.button`
  border: none;
  border-radius: 8px;
  padding: 0.5rem 0.9rem;
  cursor: pointer;
  color: white;
  background: ${(p) => (p.$secondary ? '#64748b' : '#0f766e')};
`;

const ActionLinkButton = styled.a`
  display: inline-block;
  margin-top: 0.75rem;
  border: none;
  border-radius: 8px;
  padding: 0.55rem 0.95rem;
  cursor: pointer;
  color: white;
  text-decoration: none;
  background: #0f766e;
`;

const PromptBox = styled.div`
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: #fff;
  border-radius: 16px;
  padding: 1.25rem 1.35rem;
  max-width: 720px;
  margin: 0 auto;
  box-shadow:
    0 1px 2px rgba(15, 23, 42, 0.04),
    0 12px 32px rgba(15, 23, 42, 0.07);
`;

/** Full width for campaign hero (was centered narrow column). */
const PosterCenter = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  flex: 1;
  min-height: 0;
  padding: 0 clamp(0, 2vw, 0.5rem);
`;

const BackToEventsBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #043663;
  font-weight: 600;
  font-size: 0.8125rem;
  padding: 0.45rem 0.85rem;
  margin: 0 0 1.1rem 0;
  border-radius: 10px;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  &:hover {
    border-color: rgba(4, 54, 99, 0.28);
    background: #f8fafc;
  }
`;

const LeaguesBlock = styled.div`
  min-width: 0;
`;

const PT_LAYOUT = 'playerTournamentsLayout';

function hasLeagueDeepLink(searchParams) {
  return !!(
    searchParams.get('tenant_id') ||
    searchParams.get('campaign_id') ||
    searchParams.get('season_id') ||
    searchParams.get('team_id') ||
    searchParams.get('register') ||
    searchParams.get('auto_tenant') ||
    searchParams.get('use_existing') ||
    searchParams.get('view')
  );
}

export default function PlayerTournamentsLayout() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tenantSearch, setTenantSearch] = useState('');
  const areaParam = (searchParams.get('area') || '').toLowerCase();
  const leagueDeepLink = hasLeagueDeepLink(searchParams);
  const eventsArea =
    areaParam === 'leagues' || areaParam === 'tournaments' || areaParam === 'camps'
      ? areaParam
      : leagueDeepLink
        ? 'leagues'
        : 'hub';

  const goEventsHub = () => {
    setSearchParams(new URLSearchParams(), { replace: false });
  };

  const selectEventsArea = (area) => {
    const next = new URLSearchParams();
    next.set('area', area);
    setSearchParams(next, { replace: false });
  };

  const tenantId = searchParams.get('tenant_id') || '';
  const campaignId = searchParams.get('campaign_id') || '';
  const seasonId = searchParams.get('season_id') || '';
  const teamIdFromUrl = searchParams.get('team_id') || '';
  const view = searchParams.get('view') || '';
  const registerMode = searchParams.get('register') === '1';
  const useExistingTeamMode = searchParams.get('use_existing') === '1';
  /** When set, do not auto-inject season_id for completed campaigns (fixes "Back to seasons"). */
  const skipAutoSeason = searchParams.get('skip_auto_season') === '1';
  const autoTenantHint = searchParams.get('auto_tenant') || '';

  const setParams = (updates, opts = {}) => {
    const next = new URLSearchParams(searchParams);
    if (!next.get('area') && eventsArea === 'leagues') {
      next.set('area', 'leagues');
    }
    Object.entries(updates).forEach(([k, v]) => {
      if (v == null || v === '') next.delete(k);
      else next.set(k, v);
    });
    setSearchParams(next, { replace: !!opts.replace });
  };

  const { data: tenantsRes } = useQuery(['playerTenants'], () => playerAPI.getTenants(), { staleTime: 60 * 1000 });
  const tenantsList = useMemo(() => {
    const raw = Array.isArray(tenantsRes?.data) ? tenantsRes.data : (tenantsRes?.data?.data ?? []);
    return raw.map((t) => ({
      id: t.id,
      name: t.name || t.tenant_name || '',
      slug: t.slug || '',
      tournament_logo_url: t.tournament_logo_url || null,
      country_code: t.country_code || null,
      campaigns_only_for_new_registrations: !!t.campaigns_only_for_new_registrations,
    }));
  }, [tenantsRes]);

  const { data: seasonsRes, isLoading: seasonsLoading } = useQuery(
    ['playerSeasons', tenantId],
    () => playerAPI.getSeasons(tenantId),
    { enabled: !!tenantId, staleTime: 60 * 1000 }
  );
  const seasonsList = useMemo(() => {
    if (!tenantId) return [];
    const raw = Array.isArray(seasonsRes?.data) ? seasonsRes.data : (seasonsRes?.data?.data ?? []);
    return raw.map((s) => ({
      id: s.id,
      name: s.name || s.season_name || '',
      status: s.status || null,
      myTeams: s.myTeams || [],
      is_registration_open: !!s.is_registration_open,
      is_active_now: !!s.is_active_now,
      campaign_id: s.campaign_id || null,
      campaign_title: s.campaign_title || null,
      campaign_team_eligible: !!s.campaign_team_eligible,
      registration_fee: s.registration_fee != null && s.registration_fee !== '' ? Number(s.registration_fee) : null,
      currency: s.currency && String(s.currency).trim() ? String(s.currency).trim().toUpperCase() : 'CHF',
    }));
  }, [tenantId, seasonsRes]);

  const { data: campaignsRes, isLoading: campaignsLoading } = useQuery(
    ['playerCampaigns', tenantId],
    () => playerAPI.getCampaigns(tenantId),
    { enabled: !!tenantId, staleTime: 60 * 1000 }
  );
  const campaignsRaw = Array.isArray(campaignsRes?.data) ? campaignsRes.data : campaignsRes?.data?.data ?? [];
  const campaigns = useMemo(() => (campaignsRaw || []).filter((c) => c.status === 'published'), [campaignsRaw]);

  const registrationEligibleCampaigns = useMemo(
    () => filterRegistrationEligibleCampaigns(campaigns, seasonsList),
    [campaigns, seasonsList]
  );

  const heroCampaign = registrationEligibleCampaigns[0] || null;
  const heroSeasonId = useMemo(
    () => (heroCampaign ? pickPrimaryRegistrationSeasonId(heroCampaign, seasonsList) : ''),
    [heroCampaign, seasonsList]
  );

  const { data: heroPresenceRes, isLoading: heroPresenceLoading } = useQuery(
    ['playerCampaignTeamPresence', 'hero-inline', heroCampaign?.id, tenantId, heroSeasonId],
    () => playerAPI.getCampaignTeamPresence(heroCampaign.id, tenantId, heroSeasonId || undefined),
    {
      enabled: !!tenantId && !campaignId && !!heroCampaign?.id && !!heroSeasonId,
      staleTime: 20 * 1000,
    }
  );
  const heroTeamPresence = heroPresenceRes?.data ?? {};

  const heroRegisterHref = useMemo(() => {
    if (!tenantId || !heroCampaign?.id || !heroSeasonId) return '';
    const ret = encodeURIComponent('/player/teams');
    return `/player/register-team?tenant_id=${encodeURIComponent(tenantId)}&season_id=${encodeURIComponent(
      heroSeasonId
    )}&campaign_id=${encodeURIComponent(heroCampaign.id)}&return=${ret}`;
  }, [tenantId, heroCampaign, heroSeasonId]);

  const selectedCampaign = useMemo(
    () => campaigns.find((c) => String(c.id) === String(campaignId)) || null,
    [campaigns, campaignId]
  );

  const campaignSeasons = useMemo(() => {
    if (!selectedCampaign) return [];
    const raw = selectedCampaign.seasons_by_date_desc || selectedCampaign.seasons || [];
    return raw.map((s) => ({
      id: s.season_id || s.id,
      name: s.season_name || s.name || 'Season',
      status: s.season_status || s.status || '',
      start_date: s.season_start_date || s.start_date || null,
      end_date: s.season_end_date || s.end_date || null,
    }));
  }, [selectedCampaign]);

  const campaignSeasonIds = useMemo(
    () => new Set((selectedCampaign?.seasons || []).map((s) => String(s.season_id || s.id))),
    [selectedCampaign]
  );

  /** Auto-pick open registration season for active campaigns (no manual season step). */
  const autoPickedSeasonId = useMemo(() => {
    if (!selectedCampaign || selectedCampaign.campaign_completed) return '';
    return pickPrimaryRegistrationSeasonId(selectedCampaign, seasonsList);
  }, [selectedCampaign, seasonsList]);

  useEffect(() => {
    if (!tenantId || !campaignId || seasonId || skipAutoSeason || !selectedCampaign) return;
    if (selectedCampaign.campaign_completed) return;
    if (!campaignEligibleForPlayerRegistration(selectedCampaign, seasonsList)) return;
    const chosenId = pickPrimaryRegistrationSeasonId(selectedCampaign, seasonsList);
    if (!chosenId) return;
    setParams(
      {
        season_id: chosenId,
        team_id: '',
        view: '',
        register: '',
        use_existing: '',
      },
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tenantId,
    campaignId,
    seasonId,
    skipAutoSeason,
    selectedCampaign?.id,
    selectedCampaign?.campaign_completed,
    selectedCampaign?.seasons,
    seasonsList,
  ]);

  const { data: teamsRes } = useQuery(['playerTeams'], () => playerAPI.getTeams(), { staleTime: 60 * 1000 });
  const teamsInTenant = useMemo(() => {
    const raw = Array.isArray(teamsRes?.data) ? teamsRes.data : teamsRes?.data?.data ?? [];
    return (raw || []).filter((t) => t.tenantId && tenantId && String(t.tenantId) === String(tenantId));
  }, [teamsRes, tenantId]);

  const selectedTenant = tenantsList.find((t) => t.id === tenantId) || null;
  const campaignsOnlyMode = selectedTenant?.campaigns_only_for_new_registrations === true;
  const campaignCompleted = !!selectedCampaign?.campaign_completed;

  /** Disabled or removed leagues: clear deep-linked tenant from URL. */
  useEffect(() => {
    if (!tenantId || !tenantsRes) return;
    if (tenantsList.some((t) => String(t.id) === String(tenantId))) return;
    setParams(
      {
        tenant_id: '',
        campaign_id: '',
        season_id: '',
        team_id: '',
        view: '',
        register: '',
        use_existing: '',
        skip_auto_season: '',
        auto_tenant: '',
      },
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, tenantsList, tenantsRes]);

  /** Dashboard “create team” → auto-select Helvetic Padel Series league (shows registration notice when closed). */
  useEffect(() => {
    if (!autoTenantHint || tenantId || !tenantsRes) return;
    if (autoTenantHint !== HELVETIC_AUTO_TENANT_QUERY) return;
    const hps = findHelveticPadelSeriesTenant(tenantsList);
    if (!hps) return;
    setParams(
      {
        tenant_id: hps.id,
        campaign_id: '',
        season_id: '',
        team_id: '',
        view: '',
        register: '',
        use_existing: '',
        skip_auto_season: '',
        auto_tenant: '',
      },
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoTenantHint, tenantId, tenantsList, tenantsRes]);

  /** Campaigns-only tenants: drop orphan season in URL (must pick a campaign first). */
  useEffect(() => {
    if (!tenantId || !campaignsOnlyMode) return;
    if (seasonId && !campaignId) {
      setParams(
        { season_id: '', team_id: '', view: '', register: '', use_existing: '', skip_auto_season: '' },
        { replace: true }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, campaignsOnlyMode, seasonId, campaignId]);

  /** Drop campaign/season from URL when nothing is in an open registration window (avoids showing a “live” campaign poster with closed registration). */
  useEffect(() => {
    if (!tenantId || !campaignId || !selectedCampaign) return;
    if (campaignsLoading || seasonsLoading) return;
    if (selectedCampaign.campaign_completed) return;
    if (campaignEligibleForPlayerRegistration(selectedCampaign, seasonsList)) return;
    setParams(
      {
        campaign_id: '',
        season_id: '',
        team_id: '',
        view: '',
        register: '',
        use_existing: '',
        skip_auto_season: '',
      },
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tenantId,
    campaignId,
    selectedCampaign?.id,
    selectedCampaign?.campaign_completed,
    seasonsList,
    campaignsLoading,
    seasonsLoading,
  ]);

  const { data: defaultSeasonRes, isLoading: defaultSeasonLoading } = useQuery(
    ['playerCampaignDefaultSeason', campaignId, tenantId],
    () => playerAPI.getCampaignDefaultSeason(campaignId, tenantId),
    {
      enabled:
        !!campaignId &&
        !!tenantId &&
        !!selectedCampaign?.campaign_completed &&
        !selectedCampaign?.latest_season_id &&
        !seasonId,
      staleTime: 60 * 1000,
    }
  );
  const fallbackDefaultSeasonId = defaultSeasonRes?.data?.default_season?.id || null;

  /** Season: URL wins; completed → latest/API default; active → primary registration-open linked season. */
  const resolvedSeasonId =
    seasonId ||
    (campaignCompleted ? selectedCampaign?.latest_season_id || fallbackDefaultSeasonId || '' : '') ||
    (!campaignCompleted ? autoPickedSeasonId : '') ||
    '';

  const { data: teamPresenceRes, isLoading: teamPresenceLoading } = useQuery(
    ['playerCampaignTeamPresence', campaignId, tenantId, resolvedSeasonId],
    () => playerAPI.getCampaignTeamPresence(campaignId, tenantId, resolvedSeasonId || undefined),
    { enabled: !!campaignId && !!tenantId }
  );
  const teamPresence = teamPresenceRes?.data ?? {};
  const hasCampaignTeam = !!teamPresence.has_campaign_team;

  const selectedSeason = useMemo(() => {
    if (!resolvedSeasonId) return null;
    const fromList = seasonsList.find((s) => String(s.id) === String(resolvedSeasonId));
    if (fromList) return fromList;
    const cs = (selectedCampaign?.seasons || []).find(
      (s) => String(s.season_id || s.id) === String(resolvedSeasonId)
    );
    if (cs) {
      const rowForReg = {
        season_id: cs.season_id || cs.id,
        season_status: cs.season_status,
        season_registration_start: cs.season_registration_start,
        season_registration_end: cs.season_registration_end,
        season_registration_fee: cs.season_registration_fee,
        season_currency: cs.season_currency,
      };
      return {
        id: resolvedSeasonId,
        name: cs.season_name || cs.name,
        status: cs.season_status || cs.status,
        is_registration_open: seasonRowRegistrationOpen(rowForReg, seasonsList),
        is_active_now: false,
        myTeams: [],
        registration_fee:
          cs.season_registration_fee != null && cs.season_registration_fee !== ''
            ? Number(cs.season_registration_fee)
            : null,
        currency:
          cs.season_currency && String(cs.season_currency).trim()
            ? String(cs.season_currency).trim().toUpperCase()
            : 'CHF',
      };
    }
    return null;
  }, [seasonsList, resolvedSeasonId, selectedCampaign]);

  const selectedCampaignSeason = useMemo(() => {
    if (!selectedCampaign || !resolvedSeasonId) return null;
    const all = selectedCampaign.seasons_by_date_desc || selectedCampaign.seasons || [];
    return all.find((s) => String(s.season_id || s.id) === String(resolvedSeasonId)) || null;
  }, [selectedCampaign, resolvedSeasonId]);

  const myTeams = useMemo(() => {
    if (campaignCompleted && Array.isArray(teamPresence.campaign_teams) && teamPresence.campaign_teams.length > 0) {
      return teamPresence.campaign_teams;
    }
    return selectedSeason?.myTeams || teamPresence.season_teams || [];
  }, [campaignCompleted, teamPresence, selectedSeason]);

  const selectedSeasonIsRegistrationOpen =
    !campaignCompleted &&
    (!!selectedSeason?.is_registration_open ||
      ['registration_open', 'registration'].includes(
        String(selectedCampaignSeason?.season_status || selectedSeason?.status || '').toLowerCase()
      ));

  const completedSeasonStillLoading =
    campaignCompleted &&
    !resolvedSeasonId &&
    !selectedCampaign?.latest_season_id &&
    defaultSeasonLoading;

  useEffect(() => {
    if (!selectedCampaign || !tenantId || seasonId || skipAutoSeason) return;
    if (selectedCampaign.campaign_completed && (selectedCampaign.latest_season_id || fallbackDefaultSeasonId)) {
      setParams({
        season_id: selectedCampaign.latest_season_id || fallbackDefaultSeasonId,
        team_id: '',
        view: '',
        register: '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedCampaign?.id,
    selectedCampaign?.campaign_completed,
    selectedCampaign?.latest_season_id,
    fallbackDefaultSeasonId,
    tenantId,
    seasonId,
    skipAutoSeason,
  ]);

  useEffect(() => {
    const sid = seasonId || resolvedSeasonId;
    if (!campaignId || !sid || !selectedCampaign) return;
    if (!campaignSeasonIds.has(String(sid))) {
      setParams({ season_id: '', team_id: '', view: '', register: '' }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, seasonId, resolvedSeasonId, selectedCampaign?.id, campaignSeasonIds]);

  useEffect(() => {
    if (registerMode && !selectedSeasonIsRegistrationOpen) {
      setParams({ register: '' }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerMode, selectedSeasonIsRegistrationOpen]);

  useEffect(() => {
    if (!teamIdFromUrl || !resolvedSeasonId || !tenantId || !campaignId || view || registerMode) return;
    if (myTeams.length === 0) return;
    setParams({ view: 'overview' }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamIdFromUrl, resolvedSeasonId, tenantId, campaignId, myTeams.length, view, registerMode]);

  const hasTeamForRegistrationChoice =
    hasCampaignTeam || (Array.isArray(myTeams) && myTeams.length > 0);

  const step =
    !tenantId
      ? 'tenants'
      : !campaignId
      ? 'campaigns'
      : completedSeasonStillLoading
      ? 'completed_loading'
      : !resolvedSeasonId
      ? campaignCompleted
        ? 'completed_no_season'
        : seasonsLoading &&
          campaignEligibleForPlayerRegistration(selectedCampaign, seasonsList) &&
          !skipAutoSeason
        ? 'season_resolving'
        : 'seasons'
      : selectedSeasonIsRegistrationOpen && hasTeamForRegistrationChoice && !registerMode && !useExistingTeamMode
      ? 'registration_prompt'
      : registerMode && selectedSeasonIsRegistrationOpen
      ? 'poster'
      : myTeams.length > 0 && !teamIdFromUrl
      ? 'dashboard_hint'
      : myTeams.length === 0
      ? 'no_team'
      : 'menu';

  const teamId = teamIdFromUrl || null;
  const dashboardSection = view ? VIEW_TO_SECTION[view] : null;
  const effectiveSeasonId = seasonId || resolvedSeasonId;
  const showEmbeddedDashboard = view && teamId && effectiveSeasonId && dashboardSection;
  const showNoTeamPanel = !!(tenantId && campaignId && effectiveSeasonId && myTeams.length === 0);

  const registerHref = useMemo(() => {
    const ret = encodeURIComponent('/player/teams');
    const sid = resolvedSeasonId || '';
    return `/player/register-team?tenant_id=${encodeURIComponent(tenantId)}&season_id=${encodeURIComponent(
      sid
    )}&campaign_id=${encodeURIComponent(campaignId || '')}&return=${ret}`;
  }, [tenantId, campaignId, resolvedSeasonId]);

  const showCardFlow = ['tenants', 'campaigns', 'seasons'].includes(step);

  if (eventsArea === 'hub') {
    return (
      <ContentArea>
        <PlayerEventsHub onSelect={selectEventsArea} />
      </ContentArea>
    );
  }

  if (eventsArea === 'tournaments') {
    return (
      <ContentArea>
        <PlayerOrganisedTournamentsSection showBack />
      </ContentArea>
    );
  }

  if (eventsArea === 'camps') {
    return (
      <ContentArea>
        <PlayerEventsCampsPlaceholder />
      </ContentArea>
    );
  }

  return (
    <ContentArea $embed={!!showEmbeddedDashboard}>
      {!showEmbeddedDashboard && (
        <BackToEventsBtn type="button" onClick={goEventsHub}>
          {t('playerOrganisedEvents.backToEvents', '← Back to Events')}
        </BackToEventsBtn>
      )}
      {!showEmbeddedDashboard && step !== 'poster' && (
        <>
          <PageTitle>{t('playerOrganisedEvents.leaguesSection', 'Leagues')}</PageTitle>
          {['tenants', 'campaigns', 'seasons'].includes(step) && (
            <PageSubtitle>
              <Trans i18nKey={`${PT_LAYOUT}.pageSubtitle`} components={{ strong: <strong /> }} />
              {campaignsOnlyMode && (
                <span style={{ display: 'block', marginTop: '0.6rem', color: '#0f766e', fontSize: '0.88rem' }}>
                  <Trans i18nKey={`${PT_LAYOUT}.pageSubtitleCampaignsOnly`} components={{ strong: <strong /> }} />
                </span>
              )}
            </PageSubtitle>
          )}
          {step === 'registration_prompt' && (
            <PageSubtitle style={{ marginBottom: '1rem' }}>
              <Trans i18nKey={`${PT_LAYOUT}.registrationPromptSubtitle`} components={{ strong: <strong /> }} />
            </PageSubtitle>
          )}
        </>
      )}

      <LeaguesBlock>

      {!showEmbeddedDashboard && showCardFlow && (
        <TournamentFlowPickers
          step={step}
          tenantSearch={tenantSearch}
          setTenantSearch={setTenantSearch}
          tenantsList={tenantsList}
          tenantId={tenantId}
          campaigns={campaigns}
          registrationEligibleCampaigns={registrationEligibleCampaigns}
          seasonsList={seasonsList}
          selectedCampaign={selectedCampaign}
          campaignSeasons={campaignSeasons}
          selectedTenant={selectedTenant}
          setParams={setParams}
          campaignsLoading={campaignsLoading}
          campaignCompleted={campaignCompleted}
          campaignsOnlyMode={campaignsOnlyMode}
          heroTeamPresenceLoading={heroPresenceLoading}
          heroRegisterHref={heroRegisterHref}
        />
      )}

      {!showEmbeddedDashboard && step === 'completed_loading' && (
        <Hint>{t(`${PT_LAYOUT}.loadingCampaign`)}</Hint>
      )}
      {!showEmbeddedDashboard && step === 'season_resolving' && (
        <Hint>{t(`${PT_LAYOUT}.resolvingRegistration`)}</Hint>
      )}
      {!showEmbeddedDashboard && step === 'completed_no_season' && (
        <Hint>{t(`${PT_LAYOUT}.noSeasonForCampaign`)}</Hint>
      )}

      {showEmbeddedDashboard && (
        <DashboardPage
          embedded
          embeddedShowTeamSidebar
          embeddedTeamId={teamId}
          embeddedSeasonId={effectiveSeasonId}
          embeddedCampaignId={campaignId || null}
          embeddedCampaignViewOnly={!!selectedCampaign?.campaign_completed}
          embeddedSection={dashboardSection}
          onEmbeddedSectionNavigate={(sectionKey) => {
            const v = SECTION_TO_VIEW[sectionKey];
            if (v) setParams({ view: v });
          }}
          onEmbeddedBack={() =>
            setParams({ team_id: '', view: '', register: '', use_existing: '' })
          }
        />
      )}

      {!showEmbeddedDashboard &&
        step === 'registration_prompt' &&
        tenantId &&
        campaignId &&
        resolvedSeasonId &&
        selectedCampaign && (
          <RegistrationCampaignPanel
            campaign={selectedCampaign}
            tenantName={selectedTenant?.name}
            seasonLabel={selectedSeason?.name || t('phase2.playerTournamentFlow.seasonLabelFallback')}
            presenceLoading={teamPresenceLoading}
            registerHref={registerHref}
            onBack={() =>
              setParams({
                campaign_id: '',
                season_id: '',
                team_id: '',
                view: '',
                register: '',
                use_existing: '',
                skip_auto_season: '1',
              })
            }
            includeBanner
          />
        )}

      {!showEmbeddedDashboard &&
        step === 'dashboard_hint' &&
        tenantId &&
        campaignId &&
        effectiveSeasonId && (
          <PromptBox>
            <div style={{ fontWeight: 700, color: '#0f172a', marginBottom: '0.5rem', fontSize: '1.05rem' }}>
              {campaignCompleted
                ? t(`${PT_LAYOUT}.dashboardHintTitleEnded`)
                : t(`${PT_LAYOUT}.dashboardHintTitleActive`)}
            </div>
            <div style={{ color: '#334155', fontSize: '0.92rem', lineHeight: 1.6 }}>
              {campaignCompleted ? (
                <Trans i18nKey={`${PT_LAYOUT}.dashboardHintBodyEnded`} components={{ strong: <strong /> }} />
              ) : (
                <Trans i18nKey={`${PT_LAYOUT}.dashboardHintBodyActive`} components={{ strong: <strong /> }} />
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.55rem', marginTop: '1rem', flexWrap: 'wrap' }}>
              <ActionLinkButton as={Link} to="/player/teams">
                {t(`${PT_LAYOUT}.goToMyTeams`)}
              </ActionLinkButton>
              {selectedSeasonIsRegistrationOpen && !campaignCompleted && (
                <ActionButton
                  type="button"
                  onClick={() => setParams({ register: '1', team_id: '', view: '', use_existing: '' })}
                >
                  {t(`${PT_LAYOUT}.registerTeamForSeason`)}
                </ActionButton>
              )}
              <ActionButton
                $secondary
                type="button"
                onClick={() =>
                  setParams({
                    season_id: '',
                    team_id: '',
                    view: '',
                    register: '',
                    use_existing: '',
                    skip_auto_season: '1',
                  })
                }
              >
                {t(`${PT_LAYOUT}.backToSeasons`)}
              </ActionButton>
            </div>
          </PromptBox>
        )}

      {!showEmbeddedDashboard &&
        step === 'poster' &&
        campaignId &&
        !selectedCampaign &&
        campaignsLoading && <Hint>{t(`${PT_LAYOUT}.loadingCampaign`)}</Hint>}

      {!showEmbeddedDashboard &&
        step === 'poster' &&
        campaignId &&
        !selectedCampaign &&
        !campaignsLoading && (
          <Hint>{t(`${PT_LAYOUT}.campaignUnavailable`)}</Hint>
        )}

      {!showEmbeddedDashboard && step === 'poster' && campaignId && selectedCampaign && (
        <PosterCenter>
          <CampaignPosterView
            fullBleed
            campaign={selectedCampaign}
            tenantName={selectedTenant?.name}
            seasonName={selectedSeason?.name}
            registerHref={registerHref}
            campaignTeamEligible={!!selectedCampaign.campaign_team_eligible && !registerMode}
          />
        </PosterCenter>
      )}

      {!showEmbeddedDashboard && showNoTeamPanel && (
        <EmptyState>
          <div>
            <div>
              {campaignCompleted
                ? t(`${PT_LAYOUT}.noTeamCompleted`)
                : t(`${PT_LAYOUT}.noTeamSeason`)}
            </div>
            {!campaignCompleted && selectedSeasonIsRegistrationOpen ? (
              <>
                <ActionLinkButton
                  href={registerHref}
                  onClick={(e) => {
                    if (!registerHref) {
                      e.preventDefault();
                      setParams({ register: '1', team_id: '', view: '', use_existing: '' });
                    }
                  }}
                >
                  {t(`${PT_LAYOUT}.createNewTeam`)}
                </ActionLinkButton>
              </>
            ) : !campaignCompleted ? (
              <div style={{ marginTop: '0.6rem' }}>{t(`${PT_LAYOUT}.registrationClosedSeason`)}</div>
            ) : (
              <div style={{ marginTop: '0.6rem', color: '#64748b', fontSize: '0.9rem' }}>
                {t(`${PT_LAYOUT}.registrationClosedCampaign`)}
              </div>
            )}
          </div>
        </EmptyState>
      )}

      {!showEmbeddedDashboard &&
        !showCardFlow &&
        !showNoTeamPanel &&
        step !== 'poster' &&
        step !== 'dashboard_hint' &&
        step !== 'registration_prompt' &&
        tenantId &&
        (campaignId || seasonId) &&
        !view && (
          <EmptyState>
            {step === 'no_team' && t(`${PT_LAYOUT}.noTeamInSeasonShort`)}
          </EmptyState>
        )}

      {!showEmbeddedDashboard &&
        !showNoTeamPanel &&
        step !== 'poster' &&
        tenantId &&
        (campaignId || seasonId) &&
        view &&
        !teamId &&
        step === 'menu' && (
          <EmptyState>
            {t(`${PT_LAYOUT}.noTeamMenuBody`)}
          </EmptyState>
        )}

      {!showEmbeddedDashboard && (
        <PerformanceScheduleSection>
          <PlayerLeaguePerformanceSchedule />
        </PerformanceScheduleSection>
      )}

      </LeaguesBlock>

    </ContentArea>
  );
}
